import { CreditCard, CreditCardList } from "@/api/credit-cards/credit-cards";
import { Button } from "@/components/ui/button/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import SubmitButton from "@/components/ui/submit-button";
import Typography from "@/components/ui/typography";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/auth-context";
import { creditCardKeys } from "@/features/credit-cards/keys";
import useEditCreditCard from "@/features/credit-cards/useEditCreditCard";
import { MutationStatus, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import EditCardPopup, { EditCardFormValues } from "./EditCardPopup";
import { useLoaderData } from "@tanstack/react-router";

type Props = {
	card: CreditCard;
	onDeleteConfirm: (id: number) => void;
	deleteCardStatus: MutationStatus;
	onEditConfirm?: (
		id: number,
		data: {
			card_type: string;
			exp_month: string;
			exp_year: string;
			default: boolean;
		},
	) => void;
	deleteModalOpen: boolean;
	setDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const CardInfo = ({
	card,
	onDeleteConfirm,
	deleteCardStatus,
	deleteModalOpen,
	setDeleteModalOpen,
}: Props) => {
	const { user } = useAuth();
	const [editOpen, setEditOpen] = useState(false);
	const { toast } = useToast();
	const editCardMutation = useEditCreditCard();
	const queryClient = useQueryClient();
	const { location_id } = useLoaderData({ from: "__root__" });

	function handleEditSubmit(values: EditCardFormValues) {
		editCardMutation.mutate(
			{
				exp_month: values.exp_month,
				exp_year: values.exp_year,
				guestId: user.id,
				creditCardId: card.id,
				locationId: location_id,
				user_id: user.id,
				default: values.default ? 1 : 0,
			},
			{
				onSuccess: (newCard) => {
					toast({
						title: "Success",
						description: "Your card has successfully been updated",
					});
					setEditOpen(false);
					queryClient.setQueryData(
						creditCardKeys.list({
							guestId: user.id,
						}),
						(oldData: CreditCardList) => {
							return oldData.map((oldCard) => {
								if (oldCard.id === card.id) {
									return {
										...oldCard,
										...newCard,
									};
								}
								return oldCard;
							});
						},
					);
				},
				onError: (error) => {
					toast({
						title: "Error",
						description: error.message,
					});
				},
			},
		);
	}

	return (
		<div className="w-full border border-gray-300 text-textDark rounded-2xl">
			<div className="flex justify-between border-b border-b-gray-300">
				<p className="px-4 py-2 text-sm font-medium">Card Name</p>
				<p className="px-4 py-2 text-sm font-bold">{card.card_type}</p>
			</div>
			<div className="flex justify-between border-b border-b-gray-300">
				<p className="px-4 py-2 text-sm font-medium">Card Number</p>
				<p className="px-4 py-2 text-sm font-bold">Ending in {card.last4}</p>
			</div>
			<div className="flex justify-between border-b border-b-gray-300">
				<p className="px-4 py-2 text-sm font-medium">Expiration</p>
				<p className="px-4 py-2 text-sm font-bold">
					{`${card.exp_month}/${card.exp_year}`}
				</p>
			</div>
			<div className="flex justify-between items-center">
				<Button
					variant="link"
					className="px-4 py-2 text-sm font-bold underline"
					onClick={() => setEditOpen(true)}
				>
					Edit
				</Button>
				<Button
					variant="link"
					className="text-utility-red"
					size={"sm"}
					data-testid={"remove-card-button"}
					onClick={() => setDeleteModalOpen(true)}
				>
					Remove Card
				</Button>
			</div>
			<Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							<Typography variant={"h4"}>Remove Card</Typography>
						</DialogTitle>
						<DialogDescription>
							<Typography variant={"p"}>
								Are you sure you want to remove this card? This action cannot be
								undone.
							</Typography>
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className="">
						<Button
							size={"md"}
							className=""
							variant="outline"
							onClick={() => setDeleteModalOpen(false)}
						>
							Cancel
						</Button>

						<SubmitButton
							mutationStatus={deleteCardStatus}
							loadingText="Removing..."
							testId="confirm-remove-button"
							type="button"
							onClick={() => {
								onDeleteConfirm(card?.id);
							}}
							size={"md"}
							className="mb-4"
							variant="destructive"
						>
							Remove
						</SubmitButton>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<EditCardPopup
				key={card.id}
				open={editOpen}
				updateStatus={editCardMutation.status}
				setOpen={setEditOpen}
				onSubmit={(values: EditCardFormValues) => {
					handleEditSubmit(values);
				}}
				initialValues={{
					exp_month: card.exp_month,
					default: card.default,
					exp_year: card.exp_year,
				}}
			/>
		</div>
	);
};

export default CardInfo;
